import { useEffect, useRef } from "react"
import { useFormContext, useFormState, useWatch } from "react-hook-form"
import {
  type BetterDocUsageForConsultationResult,
  useBetterDocUsageForConsultation,
} from "@/modules/patients/detail/better-doc/hooks/useBetterDocUsageForConsultation"
import { useClearSelectedCareProvider } from "@/modules/patients/detail/better-doc/hooks/useClearSelectedCareProvider"
import { getRecordedDiagnosisCode } from "@/modules/patients/detail/better-doc/lib/get-recorded-diagnosis-code"
import { isBetterDocAvailable } from "@/modules/patients/detail/better-doc/lib/is-better-doc-available"
import { isBetterDocRequired } from "@/modules/patients/detail/better-doc/lib/is-better-doc-required"
import { toResolvedBetterDocUsage } from "@/modules/patients/detail/better-doc/lib/to-resolved-better-doc-usage"
import { PointOfCareFormType } from "@/modules/patients/detail/progress-note/point-of-care/schema"
import type { EpisodeOfCareProgressNoteData } from "@/modules/patients/detail/progress-note/types/progress-note-fragment-type"
import { usePatientParam } from "@/modules/patients/hooks/usePatientParam"
import type { BetterDoc, Country, PointOfCareType } from "@/types/__generated__/union-types"

type UseApplicableBetterDocUsageParams = {
  episodeOfCareProgressNoteData: EpisodeOfCareProgressNoteData
  diagnosisCode: string
  effectivePointOfCare: PointOfCareType
  countryCode: Country | undefined
  setCareProvider: (careProvider: string | null) => void
}

export type ApplicableBetterDocUsage = BetterDocUsageForConsultationResult

const hasBetterDocUsageBecomeRequired = (
  previousBetterDocUsage: BetterDoc | null,
  currentBetterDocUsage: BetterDoc,
): boolean =>
  previousBetterDocUsage !== null &&
  !isBetterDocRequired(previousBetterDocUsage) &&
  isBetterDocRequired(currentBetterDocUsage)

export const useApplicableBetterDocUsage = ({
  episodeOfCareProgressNoteData,
  diagnosisCode,
  effectivePointOfCare,
  countryCode,
  setCareProvider,
}: UseApplicableBetterDocUsageParams): ApplicableBetterDocUsage => {
  const { control, setValue } = useFormContext<PointOfCareFormType>()
  const { isSubmitted } = useFormState({ control })
  const specialty = useWatch({ control, name: "specialtyId" })
  const resolvedBetterDocUsageInForm = useWatch({ control, name: "resolvedBetterDocUsage" })
  const patientId = usePatientParam()

  const applicableBetterDocUsage = useBetterDocUsageForConsultation({
    country: countryCode,
    patientId,
    diagnosisCode: getRecordedDiagnosisCode(episodeOfCareProgressNoteData, diagnosisCode),
    effectivePointOfCare,
    specialtyId: specialty?.id ?? null,
    enabled: isBetterDocAvailable(countryCode),
  })
  const { betterDocUsage } = applicableBetterDocUsage

  const clearSelectedCareProvider = useClearSelectedCareProvider({ episodeOfCareProgressNoteData, setCareProvider })
  const lastKnownBetterDocUsageRef = useRef<BetterDoc | null>(null)

  useEffect(() => {
    if (betterDocUsage === null) return
    const previousBetterDocUsage = lastKnownBetterDocUsageRef.current
    lastKnownBetterDocUsageRef.current = betterDocUsage
    if (hasBetterDocUsageBecomeRequired(previousBetterDocUsage, betterDocUsage)) {
      clearSelectedCareProvider()
    }
  }, [betterDocUsage, clearSelectedCareProvider])

  const resolvedBetterDocUsage = toResolvedBetterDocUsage(betterDocUsage)

  useEffect(() => {
    if (resolvedBetterDocUsage === (resolvedBetterDocUsageInForm ?? null)) return
    setValue("resolvedBetterDocUsage", resolvedBetterDocUsage, { shouldDirty: isSubmitted })
  }, [isSubmitted, resolvedBetterDocUsage, resolvedBetterDocUsageInForm, setValue])

  return applicableBetterDocUsage
}
